class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    const isMobileForm = facetForm && facetForm.id === 'FacetFiltersFormMobile';
    // Desktop and mobile: accumulate selections; apply only when "Aplicar" is clicked (no input listener).
    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    if (isMobileForm) {
      this.setupMobileFacetActions();
    } else if (facetForm && facetForm.id === 'FacetFiltersForm') {
      this.setupDesktopFacetActions();
    }
  }

  /**
   * Desktop: apply filters (no reload) when "Aplicar" is clicked. "Eliminar todos" only resets form.
   */
  setupDesktopFacetActions() {
    const form = this.querySelector('form');
    if (!form) return;
    form.addEventListener('click', (e) => {
      const applyBtn = e.target.closest('.facets__apply-btn');
      if (applyBtn) {
        e.preventDefault();
        e.stopPropagation();
        let searchParams = this.createSearchParams(form);
        const sortSelect = document.querySelector('.small-hide select[name="sort_by"]');
        if (sortSelect && !new URLSearchParams(searchParams).has('sort_by')) {
          const params = new URLSearchParams(searchParams);
          params.set('sort_by', sortSelect.value);
          searchParams = params.toString();
        }
        FacetFiltersForm.renderPage(searchParams, e);
      }
    });
  }

  /**
   * Mobile: apply filters (no reload), then close drawer. "Eliminar todos" only resets form.
   */
  setupMobileFacetActions() {
    const form = this.querySelector('form');
    if (!form) return;

    form.addEventListener('click', (e) => {
      const applyBtn = e.target.closest('.mobile-facets__apply-btn');
      if (applyBtn) {
        e.preventDefault();
        e.stopPropagation();
        const searchParams = this.createSearchParams(form);
        const promise = FacetFiltersForm.renderPage(searchParams, e);
        if (promise && typeof promise.then === 'function') {
          promise.then(() => this.closeMobileDrawer());
        } else {
          this.closeMobileDrawer();
        }
      }
    });
  }

  closeMobileDrawer() {
    const drawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
    const summary = drawer?.querySelector('details summary');
    if (summary) summary.click();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);

    // Apply sort when user changes the "Ordenar por" select (desktop or vertical form).
    document.addEventListener('change', (event) => {
      const sortSelect = event.target.closest ? event.target.closest('select[name="sort_by"]') : null;
      if (!sortSelect || sortSelect.name !== 'sort_by') return;
      const container = document.getElementById('ProductGridContainer');
      if (!container || !container.querySelector('.collection')) return;
      const params = new URLSearchParams(window.location.search);
      params.set('sort_by', sortSelect.value);
      FacetFiltersForm.renderPage(params.toString(), event);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    const promises = sections.map((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
        return Promise.resolve();
      }
      return FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    return Promise.all(promises);
  }

  static renderSectionFromFetch(url, event) {
    return fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
    FacetFiltersForm.syncActiveFacetsFromUrl();
  }

  /**
   * Ensures a pill exists for every filter value in the URL (e.g. multiple tallas).
   * Shopify's active_values may only expose one value per filter when URL has multiple.
   */
  static syncActiveFacetsFromUrl() {
    const search = window.location.search;
    if (!search) return;
    const params = new URLSearchParams(search);
    const paramKeys = [];
    params.forEach((_val, key) => {
      if (key.startsWith('filter.') && !paramKeys.includes(key)) paramKeys.push(key);
    });
    if (paramKeys.length === 0) return;

    const clearLabel = document.querySelector('.active-facets__button-remove [class="visually-hidden"]')?.textContent?.trim() || 'Clear filter';
    const labelFromParam = (key) => {
      const part = key.split('.').pop() || key;
      return part.charAt(0).toUpperCase() + part.slice(1);
    };

    const buildRemoveUrl = (paramName, valueToRemove) => {
      const newParams = new URLSearchParams();
      let removed = false;
      params.forEach((val, key) => {
        if (key === paramName && val === valueToRemove && !removed) {
          removed = true;
          return;
        }
        newParams.append(key, val);
      });
      const qs = newParams.toString();
      return window.location.pathname + (qs ? '?' + qs : '');
    };

    const hasPill = (container, label, value) => {
      const needle = `${label}: ${value}`;
      const links = container.querySelectorAll('a.active-facets__button');
      for (let i = 0; i < links.length; i++) {
        if (links[i].textContent.trim().replace(/\s+/g, ' ').includes(needle)) return true;
      }
      return false;
    };

    const escapeAttr = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const escapeHtml = (s) => {
      const div = document.createElement('div');
      div.textContent = s;
      return div.innerHTML;
    };
    const createPill = (paramName, value, removeUrl, label) => {
      const facetRemove = document.createElement('facet-remove');
      const fullUrl = removeUrl.startsWith('http') ? removeUrl : window.location.origin + removeUrl;
      facetRemove.innerHTML =
        `<a href="${escapeAttr(fullUrl)}" class="active-facets__button active-facets__button--light" data-synced-from-url="1">
          <span class="active-facets__button-inner button button--tertiary">
            ${escapeHtml(label)}: ${escapeHtml(value)}
            <span aria-hidden="true">×</span>
            <span class="visually-hidden">${escapeHtml(clearLabel)}</span>
          </span>
        </a>`;
      return facetRemove;
    };

    paramKeys.forEach((paramName) => {
      const values = params.getAll(paramName);
      const label = labelFromParam(paramName);
      const containers = document.querySelectorAll('.active-facets-desktop, .active-facets-mobile');
      const insertBefore = (container) => container.querySelector('.active-facets__button-wrapper') || container.querySelector('.active-facets__button-remove')?.closest('facet-remove') || container.lastElementChild;

      values.forEach((value) => {
        containers.forEach((container) => {
          if (hasPill(container, label, value)) return;
          const removeUrl = buildRemoveUrl(paramName, value);
          const pill = createPill(paramName, value, removeUrl, label);
          const before = insertBefore(container);
          if (before) container.insertBefore(pill, before);
          else container.appendChild(pill);
        });
      });
    });
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => FacetFiltersForm.syncActiveFacetsFromUrl());
} else {
  FacetFiltersForm.syncActiveFacetsFromUrl();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const link = this.querySelector('a');
    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const isInMobileDrawer = mobileForm && this.closest('form')?.id === 'FacetFiltersFormMobile';
    const desktopForm = document.getElementById('FacetFiltersForm');
    const isInDesktopForm = desktopForm && this.closest('form')?.id === 'FacetFiltersForm';

    if (isInMobileDrawer) {
      if (link?.hasAttribute('data-facet-clear-all')) {
        FacetRemove.resetFacetForm(mobileForm, true);
        return;
      }
      const submenu = this.closest('.mobile-facets__submenu');
      if (submenu) {
        submenu.querySelectorAll('.mobile-facets__checkbox').forEach((cb) => { cb.checked = false; });
        submenu.querySelectorAll('price-range input[type="number"]').forEach((inp) => { inp.value = ''; });
      }
      return;
    }

    if (isInDesktopForm) {
      if (link?.hasAttribute('data-facet-clear-all')) {
        FacetRemove.resetFacetForm(desktopForm, false);
        return;
      }
      const paramName = link?.getAttribute('data-param-name');
      const paramValue = link?.getAttribute('data-value');
      if (paramName && paramValue != null) {
        const inputs = desktopForm?.querySelectorAll(`input[name="${paramName}"]`);
        inputs?.forEach((inp) => {
          if (inp.value === paramValue) inp.checked = false;
        });
        return;
      }
      const filterIndex = link?.getAttribute('data-filter-index');
      if (filterIndex != null) {
        const filterEl = desktopForm?.querySelector(`.js-filter[data-index="${filterIndex}"]`);
        filterEl?.querySelectorAll('price-range input[type="number"]').forEach((inp) => { inp.value = ''; });
        return;
      }
    }

    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form?.onActiveFilterClick) form.onActiveFilterClick(event);
  }

  static resetFacetForm(form, isMobile) {
    if (!form) return;
    if (isMobile) {
      form.querySelectorAll('.mobile-facets__checkbox').forEach((cb) => { cb.checked = false; });
    } else {
      form.querySelectorAll('input[type="checkbox"][name^="filter"]').forEach((cb) => { cb.checked = false; });
    }
    form.querySelectorAll('price-range input[type="number"]').forEach((inp) => { inp.value = ''; });
  }
}

customElements.define('facet-remove', FacetRemove);
